/**
 * WizardSearchInput Component
 *
 * Reusable search input for wizard steps with MagnifyingGlassIcon.
 * Extracted from duplicated search input blocks across:
 * - AssetSelectionStep
 * - TechniqueSearchStep
 * - VulnerabilitySearchStep
 */

import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

import { cn } from '@/utils/classname';

/**
 * Props for WizardSearchInput component
 */
interface WizardSearchInputProps {
  /** Current search value */
  value: string;
  /** Callback when value changes */
  onChange: (value: string) => void;
  /** Placeholder text */
  placeholder: string;
  /** aria-label for accessibility (when no label element present) */
  ariaLabel?: string;
  /** ID of element described by this input */
  ariaDescribedBy?: string;
  /** ID of label element for this input */
  ariaLabelledBy?: string;
  /** Test ID for testing */
  testId?: string;
  /** Whether to autofocus on mount */
  autoFocus?: boolean;
}

/**
 * Reusable search input with MagnifyingGlassIcon for wizard steps.
 *
 * Provides consistent styling and accessibility attributes across
 * all wizard search inputs.
 */
export function WizardSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  ariaDescribedBy,
  ariaLabelledBy,
  testId,
  autoFocus = false,
}: WizardSearchInputProps) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="text-chariot-text-secondary absolute top-1/2 left-3 size-5 -translate-y-1/2" />
      <input
        type="text"
        className={cn(
          'bg-chariot-background-secondary border-chariot-stroke text-chariot-text-primary',
          'placeholder:text-chariot-text-tertiary w-full rounded-md border py-2.5 pr-4 pl-10 text-sm',
          'focus:border-chariot-brand-primary focus:ring-chariot-brand-primary focus:ring-1 focus:outline-hidden'
        )}
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        data-testid={testId}
        autoFocus={autoFocus}
      />
    </div>
  );
}
